import ajax from './httpService';
import { localeData } from './localeService'
import siteService from '../../control-panel/services/siteService'
const BASE_URL = 'filter';

/**
 * @typedef {'custom_time' | 'last_week' | 'last_month' | 'last_year' | 'last_day' | 'last_quarter'} TimeType
 * @typedef {import('../models/Filter').FilterOptions} FilterOptions
 */

export {
    getCompanyGeneralData,
    getCompaniesAndSites,
    getRoomListWithRoomDetails,
    normalizeAnalyticsFilter,
    buildAnalyticsArrivalQuery,
    normalizeServikNames,
    hasServikNameFilter,
    resolveServikIdFromLabel,
    resolveServikSelectionToIds,
    getServikDisplayNames,
    getFirstStatsDate,
    applyCleanerRoomsToFilter,
}

/**
 * Earliest day with shift_room_stats for a site (YYYY-MM-DD), or null.
 * @param {number|string} siteId
 * @returns {Promise<string|null>}
 */
async function getFirstStatsDate(siteId) {
    if (siteId == null || siteId === '') return null
    try {
        const data = await ajax.get(`stats/site/${siteId}/first-stats-date`)
        return data?.firstStatsDate || null
    } catch (err) {
        console.error('getFirstStatsDate failed', err)
        return null
    }
}

/**
 * Filter stores language-stable servik ids; chart labels may still be localized names.
 * @param {string|number|(string|number)[]|null|undefined} servikName
 * @returns {(string|number)[]}
 */
function normalizeServikNames(servikName) {
    if (servikName == null || servikName === '') return []
    if (Array.isArray(servikName)) return servikName.filter((value) => value != null && value !== '')
    return [servikName]
}

/** @param {string|number|(string|number)[]|null|undefined} servikName */
function hasServikNameFilter(servikName) {
    return normalizeServikNames(servikName).length > 0
}

/**
 * @param {string|number|null|undefined} label
 * @param {{ id: number|string; name: string }[]} [options]
 * @returns {number|null}
 */
function resolveServikIdFromLabel(label, options = []) {
    if (label == null || label === '') return null
    const asNumber = Number(label)
    if ((typeof label === 'number' || (typeof label === 'string' && /^\d+$/.test(label.trim()))) && Number.isFinite(asNumber)) {
        if (!options.length || options.some((option) => Number(option.id) === asNumber)) return asNumber
    }
    const byName = options.find((option) => option.name === label)
    return byName != null ? Number(byName.id) : null
}

/**
 * Migrate legacy name selections (or mixed values) to numeric ids.
 * @param {string|number|(string|number)[]|null|undefined} servikName
 * @param {{ id: number|string; name: string }[]} [options]
 * @returns {number[]}
 */
function resolveServikSelectionToIds(servikName, options = []) {
    return normalizeServikNames(servikName)
        .map((value) => resolveServikIdFromLabel(value, options))
        .filter((id) => id != null)
}

/**
 * @param {string|number|(string|number)[]|null|undefined} servikName
 * @param {{ id: number|string; name: string }[]} [options]
 * @returns {string[]}
 */
function getServikDisplayNames(servikName, options = []) {
    return normalizeServikNames(servikName).map((value) => {
        const id = resolveServikIdFromLabel(value, options)
        if (id == null) return String(value)
        const option = options.find((item) => Number(item.id) === id)
        return option?.name || String(value)
    })
}

/** @type {Map<number, number[]>} */
let cleanerRoomMap = new Map();

function parseIdList(value) {
    if (Array.isArray(value)) return value.map(Number).filter((id) => Number.isFinite(id));
    if (typeof value !== 'string' || !value || value === '[]') return [];
    try {
        return parseIdList(JSON.parse(value));
    } catch (err) {
        return [];
    }
}

function toCleanerOption(item) {
    if (!item) return null;
    const roomIds = parseIdList(item.roomIds || item.room_ids);
    if (!roomIds.length) return null;
    const name = (item.name || item.username || '').toString().trim() || item.phone || String(item.id);
    if (!name) return null;
    return { id: item.id, name, roomIds };
}

function setCleanerRoomMap(cleaners) {
    cleanerRoomMap = new Map(
        (cleaners || []).map((cleaner) => [Number(cleaner.id), cleaner.roomIds || []])
    );
}

/**
 * Resolve selected cleaners to room ids for the analytics request.
 * Leaves the stored filter unchanged so the Rooms dropdown stays empty.
 * @param {import('../models/Filter').FilterSelected} filter
 */
function applyCleanerRoomsToFilter(filter) {
    if (!filter?.cleaners?.length) return filter;
    const cleanerRooms = [...new Set(
        filter.cleaners.flatMap((id) => cleanerRoomMap.get(Number(id)) || [])
    )];
    const next = { ...filter };
    if (filter.rooms?.length) {
        const allowed = new Set(cleanerRooms);
        next.rooms = filter.rooms.map(Number).filter((id) => allowed.has(id));
    } else {
        next.rooms = cleanerRooms;
    }
    if (!next.rooms.length) next.rooms = [-1];
    return next;
}

async function getSiteCleanerOptions(siteId) {
    try {
        const contacts = await ajax.get('contact/filtered', null, { siteId });
        return (Array.isArray(contacts) ? contacts : [])
            .map(toCleanerOption)
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name));
    } catch (err) {
        return [];
    }
}

export default { getSiteCompanyList, getFilterOptions, getSiteDetails };


async function getSiteCompanyList() {
    /**@type {{ id:number | string; name: string; sites: { id: number | string; name: string; isTimeSave: boolean }[] }[]} */
    const siteCompanyList = await ajax.get(`${BASE_URL}/site-company-list`);
    return siteCompanyList;
}

/**
 * Clean filter options array by removing null, undefined, and empty values
 * @param {Array} options - Array of options (objects or primitives)
 * @returns {Array} - Cleaned array
 */
function cleanFilterOptions(options) {
    if (!Array.isArray(options)) return [];
    
    return options.filter(item => {
        // Handle null or undefined
        if (item === null || item === undefined) return false;
        
        // Handle object items (with id and name)
        if (typeof item === 'object') {
            // Check if id is valid
            const hasValidId = item.id !== null && item.id !== undefined && item.id !== '';
            
            // Check if name is valid (not null, undefined, empty string, or whitespace)
            const name = item.name;
            const hasValidName = name !== null && 
                                name !== undefined && 
                                name !== '' && 
                                String(name).trim() !== '';
            
            return hasValidId && hasValidName;
        }
        
        // Handle primitive values (strings, numbers)
        if (typeof item === 'string' && item.trim() === '') return false;
        
        return true;
    });
}

/**
 * @param {number | string} siteId 
 * @param {{ id: number | string; name: string; sites: { id:number | string; name: string; }[] }[]} companies
 * @returns {Promise<FilterOptions>}
*/
async function getFilterOptions(siteId, companies) {
    const lang = localeData?.language || 'en'
    const filterOptions = await ajax.get(`${BASE_URL}/options/${siteId}`, null, { lang });
    filterOptions.timeType = ['custom_time', 'last_day', 'last_week', 'last_month', 'last_quarter']
    filterOptions.viewBy = [
        { label: "view_by_years", value: "year" },
        { label: "view_by_months", value: "month" },
        { label: "view_by_weeks", value: "week" },
        { label: "view_by_days", value: "day" }
      ];
    filterOptions.companies = companies;
    if (!Array.isArray(filterOptions.servikName)) {
        filterOptions.servikName = [];
    }
    
    // Clean all filter option arrays to remove null/empty values
    if (Array.isArray(filterOptions.building)) {
        filterOptions.building = cleanFilterOptions(filterOptions.building);
    }
    if (Array.isArray(filterOptions.floor)) {
        filterOptions.floor = cleanFilterOptions(filterOptions.floor);
    }
    if (Array.isArray(filterOptions.rooms)) {
        filterOptions.rooms = cleanFilterOptions(filterOptions.rooms);
    }
    if (Array.isArray(filterOptions.gender)) {
        filterOptions.gender = cleanFilterOptions(filterOptions.gender);
    }
    if (Array.isArray(filterOptions.shift)) {
        filterOptions.shift = cleanFilterOptions(filterOptions.shift);
    }
    if (Array.isArray(filterOptions.days)) {
        filterOptions.days = cleanFilterOptions(filterOptions.days);
    }
    if (Array.isArray(filterOptions.servikName)) {
        filterOptions.servikName = cleanFilterOptions(filterOptions.servikName);
    }
    const optionCleaners = (filterOptions.cleaners || []).map(toCleanerOption).filter(Boolean);
    filterOptions.cleaners = optionCleaners.length ? optionCleaners : await getSiteCleanerOptions(siteId);
    setCleanerRoomMap(filterOptions.cleaners);
    
    return filterOptions
}

async function getCompanyGeneralData(filter) {
    filter.language = localeData.language
    const res = await ajax.get('company/general', null, filter)
    return res
}

async function getCompaniesAndSites() {
    try {
        const data = await ajax.get('company/general')
        const sitesCompanyMap = data.reduce((acc, item) => {
            const { company_id, company_name, site_id, site_name } = item
            if (!acc[company_id]) acc[company_id] = {
                company_id,
                company_name,
                sites: []
            }
            acc[company_id].sites.push({ site_id, site_name })
            return acc
        }, {})

        return Object.values(sitesCompanyMap)
    } catch (err) {
        return [];
    }

}

/**
* @param {{
* companyId:String | Number;
* siteId?:String | Number;
* floor?:String;
* zone?:String;
* building?: String;
* gender?:String;
* }} filter
*/
async function getRoomListWithRoomDetails(filter) {
    /**
    * @type {{
    * companyId:String | Number;
    * siteId?:String | Number;
    * floor?:String[];
    * zone?:String[];
    * building?: String[];
    * gender?:String[];
    * }}
    */
    const query = {};
    const { building, floor, gender, zone, companyId, siteId } = filter;
    if (building) query.building = JSON.parse(building);
    if (floor) query.floor = JSON.parse(floor);
    if (gender) query.gender = JSON.parse(gender);
    if (zone) query.zone = JSON.parse(zone);
    if (siteId) query.siteId = siteId;
    query.companyId = companyId;
    const rooms = await ajax.get('room/staticRooms', null, query);
    return rooms;
}

async function getSiteDetails(id) {
    return await siteService.getSiteById(id)
}

/**
 * Normalize a filter object for stable comparison (e.g. export vs loaded analytics).
 * @param {Record<string, unknown>} filter
 * @returns {string}
 */
function normalizeAnalyticsFilter(filter) {
    const normalized = {};
    for (const key in filter) {
        if (key === 'language' || key === 'roomId') continue;
        const value = filter[key];
        if (Array.isArray(value)) {
            normalized[key] = value.length > 0 ? [...value].sort().join(',') : null;
        } else {
            normalized[key] = value;
        }
    }
    return JSON.stringify(normalized);
}

/**
 * Build the filter for the site-wide arrivals export API from the active filter panel state.
 * Mirrors the per-room Cleaner Arrival dashboard page, which keys off the room set, shifts, days and
 * date range only - so servikName/hour drill-down filters are intentionally excluded. `time` is sent
 * as an object (axios serializes it to JSON, matching the other analytics endpoints).
 * @param {import('../models/Filter').FilterSelected} filterSelected
 * @param {{ language?: string }} [opts]
 */
function buildAnalyticsArrivalQuery(filterSelected, opts = {}) {
    if (!filterSelected) return {};
    const query = {
        site: filterSelected.site,
        time: filterSelected.time || {},
    };
    if (opts.language) query.language = opts.language;
    const withCleanerRooms = applyCleanerRoomsToFilter(filterSelected);
    if (withCleanerRooms.rooms?.length) query.rooms = withCleanerRooms.rooms;
    if (filterSelected.building) query.building = filterSelected.building;
    if (filterSelected.floor?.length) query.floor = filterSelected.floor;
    if (filterSelected.gender?.length) query.gender = filterSelected.gender;
    if (filterSelected.shift?.length) query.shift = filterSelected.shift;
    if (filterSelected.days?.length) query.days = filterSelected.days;
    if (filterSelected.cleaners?.length) query.cleaners = filterSelected.cleaners;
    return query;
}